'use client';

import { Button, ButtonProps } from '@mui/material';

import { styled } from '../../styles';
import { Theme } from '../../theme';
import {
  EXTRA_SMALL_INPUT_HEIGHT,
  LARGE_INPUT_HEIGHT,
  MEDIUM_INPUT_HEIGHT,
  SMALL_INPUT_HEIGHT,
} from '../../theme/constants';
import { Loader } from '../Loader';
import { Typography } from '../Typography';

const getHeight = ({ size }: ButtonProps) => {
  if (size === 'large') {
    return `${LARGE_INPUT_HEIGHT}px`;
  }

  if (size === 'medium') {
    return `${MEDIUM_INPUT_HEIGHT}px`;
  }

  if (size === 'small') {
    return `${SMALL_INPUT_HEIGHT}px`;
  }

  return `${EXTRA_SMALL_INPUT_HEIGHT}px`;
};

const getPadding = (props: ButtonProps & { theme: Theme }) => {
  const { size, theme, variant } = props;

  if (size === 'large') {
    return theme.spacing(5, 4);
  }

  if (size === 'medium') {
    return theme.spacing(3, 4);
  }

  if (size === 'small' && variant === 'text') {
    return theme.spacing(1, 3);
  }

  if (size === 'extraSmall' && variant === 'text') {
    return theme.spacing(1, 1);
  }

  return theme.spacing(1, 3);
};

const getBorderRadius = (props: ButtonProps & { theme: Theme }) => {
  const { size, theme } = props;

  if (size === 'large' || size === 'medium') {
    return theme.shape.borderRadius * 2;
  }

  return theme.shape.borderRadius;
};

const getGap = (props: ButtonProps & { theme: Theme }) => {
  const { size, theme } = props;

  if (size === 'small' || size === 'extraSmall') {
    return theme.spacing(1);
  }

  return theme.spacing(2);
};

export const StyledButton = styled(Button)<ButtonProps>((props) => ({
  height: getHeight(props),
  padding: getPadding(props),
  borderRadius: getBorderRadius(props),
  boxShadow: 'none',

  ...(props.variant === 'text' && {
    height: 'auto',
    backgroundColor: 'transparent',

    '&:hover': {
      height: 'auto',
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
  }),

  ...(props.variant === 'contained' && {
    '&:hover': {
      color: props.theme.palette.textWhite,
      boxShadow: 'none',
    },
  }),

  '&:disabled': {
    color: props.theme.palette.text3,
  },

  '& .MuiTypography-root': {
    gap: getGap(props),
  },
}));

export const ButtonTypography = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  width: 'max-content',
}));

export const ButtonHiddenContainer = styled.div(
  () =>
    ({
      visibility: 'hidden',
    } as const),
);

export const ButtonLoader = styled(Loader)(
  () =>
    ({
      position: 'absolute',
    } as const),
);

// custom button for dark theme landing only
export const StyledOutlinedBlurredButton = styled(Button)((props) => ({
  height: getHeight(props),
  padding: getPadding(props),
  borderRadius: getBorderRadius(props),
  borderColor: props.theme.palette.grey?.[700],
  backdropFilter: 'blur(20px)',
  color: props.theme.palette.grey?.[100],
  overflow: 'hidden',

  '&:hover': {
    borderColor: props.theme.palette.stateHover,
    color: props.theme.palette.stateNormal,
    background: 'rgba(48, 113, 238, 0.10)',
  },
}));
